import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Card,
  CardTitle,
  CardBody,
  Col,
  Input,
  Row,
  Label,
  Button,
  FormGroup,
} from "reactstrap";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { FormattedMessage, useIntl } from "react-intl";
import { PostNotification } from "../../actions/superUserMutations";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatDate } from "../../Utilities/dateUtils";
import { useQueryClient } from "@tanstack/react-query";
import { compareStrings } from "../../Utilities/sortHelper";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

export default function AdminNotificationsCreate({ schoolData, licenseData }) {
  const intl = useIntl();
  const schools = useMemo(() => schoolData?.schools ?? [], [schoolData]);
  const [notificationText, setNotificationText] = useState(null);
  const [notificationTitle, setNotificationTitle] = useState(null);
  const [notificationImage, setNotificationImage] = useState(null);
  const [notificationVideo, setNotificationVideo] = useState(null);
  const [forUserType, setForUserType] = useState(null);
  const [notificationDateForShow, setNotificationDateForShow] = useState(
    new Date()
  );

  const [schoolOptions, setSchoolOptions] = useState([]);
  const [licenseOptions, setLicenseOptions] = useState([]);
  const [mediaType, setMediaType] = useState("none");
  const [isIntrusive, setIsIntrusive] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [formKey, setFormKey] = useState(0);

  const licenseRef = useRef();
  const schoolRef = useRef();
  const queryClient = useQueryClient();

  useEffect(() => {
    let tempSchoolOptions = [];
    let tempLicensOptions = [];

    schools.forEach((school) => {
      let schoolOption = { value: school.id, label: school.name };
      tempSchoolOptions.push(schoolOption);
    });
    let allSchoolsOption = {
      value: "all",
      label: "Alla skolor",
    };
    // Sortera före unshift så att "Alla skolor" ligger kvar överst.
    tempSchoolOptions.sort((a, b) => compareStrings(a.label, b.label));
    tempSchoolOptions.unshift(allSchoolsOption);
    setSchoolOptions(tempSchoolOptions);
    licenseData.forEach((license) => {
      let licenseOption = { value: license.id, label: license.programName };
      tempLicensOptions.push(licenseOption);
    });
    let allLicensesOption = {
      value: "all",
      label: "Alla programlicenser",
    };
    tempLicensOptions.unshift(allLicensesOption);
    setLicenseOptions(tempLicensOptions);
  }, [schools, licenseData]);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
  }), []);

  const handlePostNotification = useMutation({ mutationFn: PostNotification });

  const handleMediaSelect = () => {
    let retVal = { image: null, video: null };
    if (mediaType === "image") {
      retVal.image = notificationImage;
      retVal.video = null;
    } else if (mediaType === "video") {
      retVal.video = notificationVideo;
      retVal.Image = null;
    } else {
      retVal.video = null;
      retVal.image = null;
    }
    return retVal;
  };

  const handleLicensesSelect = () => {
    let selectedLicenses = licenseRef.current.getValue();
    let retVal = [];
    if (selectedLicenses.some((lic) => lic.value === "all")) {
      licenseData.forEach((license) => {
        retVal.push(license.id);
      });
    } else {
      selectedLicenses.forEach((selLic) => {
        retVal.push(selLic.value);
      });
    }
    return retVal;
  };

  const handleSelectForUserType = (userType) => {
    setForUserType(userType);
    setValidationErrors(validationErrors.filter((val) => val !== "userType"));
  };

  const handleTitleChange = (title) => {
    setNotificationTitle(title);
    setValidationErrors(validationErrors.filter((val) => val !== "title"));
  };

  const handlePost = () => {
    let media = handleMediaSelect();
    let licenses = handleLicensesSelect();
    let forAllSchools = schoolRef.current
      .getValue()
      .some((val) => val.value === "all");
    let schoolIds = [];
    let selectedSchools = schoolRef.current.getValue();
    selectedSchools.forEach((school) => {
      schoolIds.push(school.value);
    });
    let postObject = {
      title: notificationTitle,
      text: notificationText,
      image: media.image,
      video: media.video,
      forLicenses: licenses,
      forSchools: forAllSchools ? [] : schoolIds,
      showForTeachers: forUserType === "teachers" || forUserType === "all",
      showForStudents: forUserType === "students" || forUserType === "all",
      showForAllCustomers: forAllSchools,
      showFrom: formatDate(notificationDateForShow, "yyyy-MM-dd"),
      isIntrusive: isIntrusive,
    };
    let error = false;
    let tempErrors = [...validationErrors];
    if (notificationTitle === null || notificationTitle.trim() === "") {
      tempErrors = [...tempErrors, "title"];
      error = true;
    } else {
      tempErrors = tempErrors.filter((val) => val !== "title");
    }
    if (
      schoolRef.current.getValue() === null ||
      schoolRef.current.getValue().length < 1
    ) {
      tempErrors = [...tempErrors, "schools"];
      error = true;
    } else {
      tempErrors = tempErrors.filter((val) => val !== "schools");
    }
    if (forUserType === null) {
      tempErrors = [...tempErrors, "userType"];
      error = true;
    } else {
      tempErrors = tempErrors.filter((val) => val !== "userType");
    }
    if (licenses.length < 1) {
      tempErrors = [...tempErrors, "licenses"];
      error = true;
    } else {
      tempErrors = tempErrors.filter((val) => val !== "licenses");
    }
    setValidationErrors(tempErrors);

    if (!error) {
      handlePostNotification.mutate(
        {
          postObject,
        },
        {
          onSuccess: () => {
            toast.success("Notis postad");
            queryClient.invalidateQueries({ queryKey: ["FetchNotifications"] });
            setFormKey((prev) => prev + 1);
            setNotificationText(null);
            setNotificationTitle(null);
            setNotificationImage(null);
            setNotificationVideo(null);
            setForUserType(null);
            setNotificationDateForShow(new Date());
            setMediaType("none");
            setIsIntrusive(false);
            setValidationErrors([]);
          },
          onError: (err) => {
            toast.warn(formatServiceError(intl, err?.response?.data));
          },
        }
      );
    }
  };

  const mediaChooser = () => {
    if (mediaType === "image") {
      return (
        <Input
          type="file"
          onChange={(data) => setNotificationImage(data.target.files[0])}
        />
      );
    } else if (mediaType === "video") {
      return (
        <Input
          type="text"
          placeholder="ex. https://www.youtube.com/watch?v=dQw4w9WgXcQ"
          onChange={(data) => setNotificationVideo(data.target.value)}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Card key={formKey} style={{ zIndex: "9" }}>
      <CardBody>
        <CardTitle>
          <FormattedMessage id="CreateNewNotification" />
        </CardTitle>
        <Row>
          <Col lg="5">
            <p className="lead" style={{ marginBottom: ".3rem" }}>
              <FormattedMessage id="Content" />
            </p>
            <hr style={{ marginTop: ".3rem" }} />
          </Col>
        </Row>
        <Row>
          <Col lg="4">
            <Input
              placeholder="Rubrik"
              name="Title"
              type="text"
              onChange={(data) => handleTitleChange(data.target.value)}
            />
            {validationErrors.some((val) => val === "title") ? (
              <Label for="Title" style={{ color: "red" }}>
                <FormattedMessage id="YouHaveToEnterTitleForNotification" />.
              </Label>
            ) : null}
          </Col>
        </Row>
        <Row className="mt-3">
          <Col lg="6">
            <ReactQuill
              theme="snow"
              value={notificationText || ""}
              onChange={setNotificationText}
              modules={quillModules}
              placeholder="Text"
              className="notifications--editor"
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col sm="2">
            <FormGroup tag="fieldset">
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="mediaTypeRadio"
                    defaultChecked={true}
                    onChange={() => setMediaType("none")}
                  />{" "}
                  <FormattedMessage id="None" />
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="mediaTypeRadio"
                    onChange={() => setMediaType("image")}
                  />{" "}
                  <FormattedMessage id="Image" />
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="mediaTypeRadio"
                    onChange={() => setMediaType("video")}
                  />{" "}
                  <FormattedMessage id="Video" />
                </Label>
              </FormGroup>
            </FormGroup>
          </Col>
          <Col>{mediaChooser()}</Col>
        </Row>

        <Row>
          <Col lg="5">
            <p className="lead" style={{ marginBottom: ".3rem" }}>
              <FormattedMessage id="Display" />
            </p>
            <hr style={{ marginTop: ".3rem" }} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Label for="date">
              <FormattedMessage id="FromWhatDateShouldNotificationBeVisible" />?
            </Label>
            <DatePicker
              selected={notificationDateForShow}
              onChange={(value) => setNotificationDateForShow(value)}
              locale="sv"
              dateFormat="yyyy-MM-dd"
              className="input date--picker"
            />
          </Col>
        </Row>
        <Row className="mt-1">
          <Col>
            <Label for="schools">
              <FormattedMessage id="ForWhichSchoolShouldNotificationBeVisible" />
              ?
            </Label>
            <Select
              classNamePrefix={
                validationErrors.some((val) => val === "schools")
                  ? "react-select--error"
                  : ""
              }
              name="schools"
              ref={schoolRef}
              onChange={() =>
                setValidationErrors(
                  validationErrors.filter((val) => val !== "schools")
                )
              }
              placeholder={intl.formatMessage({ id: "ChooseSchools" })}
              isMulti
              options={schoolOptions}
            />
            {validationErrors.some((val) => val === "schools") ? (
              <Label for="schools" style={{ color: "red" }}>
                <FormattedMessage id="YouHaveToSelectSchoolForNotification" />.
              </Label>
            ) : null}
          </Col>
          <Col>
            <Label for="licenses">
              <FormattedMessage id="ForWhichProgramlicensesShouldNotificationBeVisible" />
              ?
            </Label>
            <Select
              classNamePrefix={
                validationErrors.some((val) => val === "licenses")
                  ? "react-select--error"
                  : ""
              }
              name="licenses"
              ref={licenseRef}
              placeholder={intl.formatMessage({ id: "ChooseProgramLicenses" })}
              isMulti
              options={licenseOptions}
              onChange={() =>
                setValidationErrors(
                  validationErrors.filter((val) => val !== "licenses")
                )
              }
            />
            {validationErrors.some((val) => val === "licenses") && (
              <Label for="licenses" style={{ color: "red" }}>
                <FormattedMessage id="YouHaveToSelectLicensesForNotification" />
                .
              </Label>
            )}
          </Col>
          <Col>
            <Label for="userTypes">
              <FormattedMessage id="ForWhichsertypesShouldNotificationBeVisible" />
              ?
            </Label>
            <Select
              name="userTypes"
              classNamePrefix={
                validationErrors.some((val) => val === "userType")
                  ? "react-select--error"
                  : ""
              }
              placeholder={intl.formatMessage({ id: "ChooseUser" })}
              onChange={(val) => handleSelectForUserType(val.value)}
              options={[
                { value: "all", label: intl.formatMessage({ id: "All" }) },
                { value: "teachers", label: intl.formatMessage({ id: "Teachers" }) },
                { value: "students", label: intl.formatMessage({ id: "Students" }) },
              ]}
            />
            {validationErrors.some((val) => val === "userType") && (
              <Label for="userTypes" style={{ color: "red" }}>
                <FormattedMessage id="YouHaveToSelectUsersForNotification" />.
              </Label>
            )}
          </Col>
        </Row>

        <Row className="mt-2">
          <Col lg="4">
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={isIntrusive}
                  onChange={() => setIsIntrusive(!isIntrusive)}
                />{" "}
                <FormattedMessage id="IntrusiveNotification" />
              </Label>
            </FormGroup>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col lg="3">
            <Button color={"primary"} outline onClick={() => handlePost()}>
              {" "}
              <FormattedMessage id="PostNotification" />
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
